import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {useCallback, useEffect, useRef} from 'react'

import Combobox from '../../components/combobox'
import {useComboboxMenuVisibilityBehavior} from './use-combobox-menu-visibility-behavior'
import {useControlledProp} from './use-controlled-prop'
import {useProvidedRefOrCreate} from './use-provided-ref-or-create'

let nextId = 1

export interface UseAutocompleteProps<T> {
  items: Array<T>
  onSelectedItemChange: (item: T) => void
  isOpen?: boolean
  inputValue?: string
}

export interface GetInputPropsAdditionalHandlers {
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export interface GetItemPropsAdditionalHandlers {
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void
  onMouseUp?: (e: React.MouseEvent<HTMLElement>) => void
}

const handleEvent = <TEvent, TUserProps>(
  event: TEvent,
  ownHandler: (event: TEvent) => void,
  userProps: TUserProps | undefined,
  key: keyof TUserProps,
) => {
  if (userProps?.[key]) {
    const handler = userProps[key]
    if (typeof handler === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      handler(event)
    }
  }
  ownHandler(event)
}

/**
 * A hook which provides behavior compliant with the ARIA combo box Design Pattern:
 * (https://www.w3.org/TR/wai-aria-practices/#combobox).
 * In addition to the state of the menu and current input value,
 * useAutocomplete returns a set of props getter functions which should be applied to the relevant
 * elements in order to provide the correct behavior.
 * Builds on top of useComboboxMenuVisibilityBehavior to handle the visibility of the menu items
 * @param props - Requires a list of items, and onSelectedItemChange callback. Optionally accepts isOpen
 * and inputValue. For each of these that are provided, state of those parameters are delegated to the consumer,
 * rather than tracked internally in the hook.
 * @param userInputRef An optional ref to the input to which the behavior is being added.
 * If not provided, will be created and tracked by the hook.
 */
export const useAutocomplete = <T>(
  props: UseAutocompleteProps<T>,
  userInputRef?: React.RefObject<HTMLInputElement | null>,
) => {
  const [inputValue, setInputValue] = useControlledProp('inputValue', '', props)
  const {isOpen, setIsOpen, inputOnBlur, inputOnFocus, itemOnMouseDown, onResetListInteraction} =
    useComboboxMenuVisibilityBehavior(props)

  const listRef = useRef<HTMLUListElement>(null)
  const itemsRef = useRef<Array<T>>(props.items)
  const comboboxRef = useRef<Combobox | undefined>(undefined)

  const onSelectedItemChangeRef = useRef<(item: T) => void>(props.onSelectedItemChange)

  const id = useRef(nextId++)
  const inputId = useRef(`combobox-input-${id.current}`)
  const listId = useRef(`combobox-list-${id.current}`)

  const inputRef = useProvidedRefOrCreate(userInputRef)

  useEffect(() => {
    itemsRef.current = props.items
  }, [props.items])

  if (onSelectedItemChangeRef.current !== props.onSelectedItemChange) {
    onSelectedItemChangeRef.current = props.onSelectedItemChange
  }

  const selectItem = useCallback(
    (index: number) => {
      if (itemsRef.current) {
        const selectedItem = itemsRef.current[index]
        if (selectedItem) {
          onSelectedItemChangeRef.current(selectedItem)
        }

        onResetListInteraction()

        if (inputRef.current) {
          inputRef.current.focus()
        }
        setIsOpen(false)
      }
    },
    [onResetListInteraction, setIsOpen, inputRef],
  )

  const onComboboxCommit = useCallback(
    (e: Event) => {
      const dataListIndex = (e.target as HTMLElement).getAttribute('data-list-index')
      if (dataListIndex) {
        selectItem(parseInt(dataListIndex))
      }
    },
    [selectItem],
  )

  const trackingOnComboboxCommit = useTrackingRef(onComboboxCommit)

  useEffect(() => {
    const list = listRef.current
    const input = inputRef.current
    if (input && list) {
      comboboxRef.current = new Combobox(input, list, {
        firstOptionSelectionMode: 'selected',
        tabInsertsSuggestions: false,
      })

      const handler = (e: Event) => {
        trackingOnComboboxCommit.current(e)
      }
      list.addEventListener('combobox-commit', handler)

      return function cleanup() {
        // Remove DOM listeners
        comboboxRef.current?.destroy()
        // Remove component in the React lifecycle, so this useEffect can run again idempotently
        comboboxRef.current = undefined
        list.removeEventListener('combobox-commit', handler)
      }
    }
    // Run once on mount to initialize Combobox and event listeners.

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isOpen) {
      comboboxRef.current?.start()
    } else {
      comboboxRef.current?.stop()
    }
  }, [isOpen])

  const inputOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      setIsOpen(true)
    },
    [setInputValue, setIsOpen],
  )

  const getInputProps = useCallback(
    (additionalHandlers?: GetInputPropsAdditionalHandlers) => {
      return {
        ref: inputRef,
        id: inputId.current,
        type: 'text',
        value: inputValue,
        onFocus: (e: React.ChangeEvent<HTMLInputElement>) => {
          handleEvent(e, inputOnFocus, additionalHandlers, 'onFocus')
        },
        onBlur: (e: React.ChangeEvent<HTMLInputElement>) => {
          handleEvent(e, inputOnBlur, additionalHandlers, 'onBlur')
        },
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          handleEvent(e, inputOnChange, additionalHandlers, 'onChange')
        },
      }
    },
    [inputOnFocus, inputOnBlur, inputOnChange, inputValue, inputRef],
  )
  const getListProps = () => {
    return {ref: listRef, id: listId.current}
  }
  const getItemProps = (_item: T, index: number, additionalHandlers?: GetItemPropsAdditionalHandlers) => {
    return {
      role: 'option',
      id: `combobox-${id.current}-item-${index}`,
      'data-list-index': index,
      onMouseDown: (e: React.MouseEvent<HTMLLIElement>) => {
        handleEvent(e, itemOnMouseDown, additionalHandlers, 'onMouseDown')
      },
    }
  }

  const selectFirstItem = useCallback(() => {
    const combobox = comboboxRef.current
    if (combobox && inputRef.current && listRef.current) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        combobox.clearSelection()
        combobox.navigate(1)
      })
    }
  }, [inputRef])

  return {inputValue, setInputValue, getInputProps, getListProps, getItemProps, isOpen, selectFirstItem}
}
